import { useEffect, useMemo } from "react";
import { GroupNavigationTile, GroupIconType } from "./GroupNavigationTile/GroupNavigationTile";
import "./GroupManagementDashboard.scss";
import { TimeStamp } from "../../../common/components/TimeStamp/TimeStamp";
import { useTranslation } from "react-i18next";
import defaultContent from "../../../content/groupManagement";
import { getCanonicalTitle } from "../../../common/util/Incentives/IncentiveRegistry";
import titleContent from "../../../content/performanceIncomeDashboard";
import { ROUTE_PATHS } from "../../../common/enums/routing-enums";
import LeafTile from "../../../common/leaf/LeafTile/LeafTile";
import { ErrorTile } from "../../../common/components/ErrorTile/ErrorTile";
import { useGroupManagementOverview } from "./GroupManagementOverviewDataLayer";
import { CustomerOrdersCard } from "./CustomerOrders/CustomerOrdersWrapper/CustomerOrdersWrapper";
import { ActionCenter } from "./ActionCenter/ActionCenter";
import { GroupNavigationTileSkeletonList } from "./GroupNavigationTile/GroupNavigationTileSkeleton";
import { ActionCardSkeletonList } from "./ActionCard/ActionCardSkeleton";
import LeafRectangleLoader from "../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { BonusInfoFooter } from "../../../common/components/BonusInfoFooter/BonusInfoFooter";
import { PageTitleSection } from "../../../common/components/PageTitleSection/PageTitleSection";
import TEALIUM from "../../../common/enums/tealium-enums";
import { track } from "../../../common/util/tealium";

const allNavigationTiles = [
  {
    id: "los",
    contentKey: "viewLos",
    navKey: "los" as const,
    iconType: GroupIconType.Los,
    to: ROUTE_PATHS.LOS,
    tealiumAction: "view_los",
    tealiumCategory: "rwlos",
  },
  {
    id: "edlos",
    contentKey: "viewEmeraldDiamondLos",
    navKey: "edlos" as const,
    iconType: GroupIconType.EdLos,
    to: ROUTE_PATHS.EDLOS,
    tealiumAction: "view_emerald_diamond_los",
    tealiumCategory: "edlos",
  },
  {
    id: "watchlist",
    contentKey: "viewWatchlist",
    navKey: "watchlist" as const,
    iconType: GroupIconType.Watchlist,
    to: ROUTE_PATHS.WATCHLIST,
    tealiumAction: "view_watchlist",
    tealiumCategory: "watchlist",
  },
  {
    id: "customerMemberList",
    contentKey: "viewCustomerList",
    navKey: "customerMemberList" as const,
    iconType: GroupIconType.CustomerList,
    to: ROUTE_PATHS.CUSTOMER_LIST,
    tealiumAction: "view_customer_list",
    tealiumCategory: "global_cml",
  },
];

export const GroupManagementDashboard = () => {
  const { t } = useTranslation(["groupManagement", "performanceIncomeDashboard"]);
  const { isLoading, errorStatus, groupManagementOverviewData, refetch } = useGroupManagementOverview();

  useEffect(() => {
    track(
      TEALIUM.U_TAG_METHOD.VIEW,
      "",
      TEALIUM.CATEGORY.GROUP_MANAGEMENT,
      "group_management_dashboard",
      "group_management",
      "",
    );
  }, []);

  const customerListItem = groupManagementOverviewData.NavigationItems.find((item) => item.Id === "customerMemberList");
  const isCustomerListEnabled = customerListItem ? customerListItem.Enabled : false;

  const navigationItemMap = useMemo(() => {
    const map = new Map<string, boolean>();
    groupManagementOverviewData.NavigationItems.forEach((item) => {
      map.set(item.Id, item.Enabled);
    });
    return map;
  }, [groupManagementOverviewData.NavigationItems]);

  const navigationTiles = useMemo(
    () => allNavigationTiles.filter((tile) => navigationItemMap.has(tile.id)),
    [navigationItemMap],
  );

  if (errorStatus) {
    return (
      <LeafTile hideBorder={true}>
        <ErrorTile
          clickFunction={refetch}
          errorStatus={errorStatus}
          isLoading={isLoading}
          tealiumTrackId="GroupManagement_Overview"
        />
      </LeafTile>
    );
  }

  if (isLoading) {
    return (
      <div className="GroupManagementDashboard">
        <div className="GroupManagementDashboard__heading-row">
          <PageTitleSection
            title={`${t("tabs.group", titleContent.tabs.group as string, { ns: "performanceIncomeDashboard" })}`}
            hideTimeStamp
          />
        </div>
        <div className="GroupManagementDashboard__section-divider">
          <div className="GroupManagementDashboard__section-header">
            <h2 className="GroupManagementDashboard__section-title">
              {t("actionCenter", defaultContent.actionCenter as string)}
            </h2>
          </div>
          <div className="GroupManagementDashboard__section-description">
            <LeafRectangleLoader isLoading height="1.15rem" width="15.625rem" margin="0" />
          </div>
        </div>
        <div className="GroupManagementDashboard__action-center">
          <ActionCardSkeletonList />
        </div>
        <div className="GroupManagementDashboard__customer-orders">
          <CustomerOrdersCard isOverviewLoading isCustomerListEnabled={isCustomerListEnabled} />
        </div>
        <div className="GroupManagementDashboard__navigation-tiles">
          <GroupNavigationTileSkeletonList />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="GroupManagementDashboard" data-testid="group-management-dashboard">
        <div className="GroupManagementDashboard__heading-row">
          <PageTitleSection
            title={`${t("tabs.group", titleContent.tabs.group as string, { ns: "performanceIncomeDashboard" })}`}
            hideTimeStamp
          />
        </div>
        <div className="GroupManagementDashboard__section-divider">
          <div className="GroupManagementDashboard__section-header">
            <h2 className="GroupManagementDashboard__section-title">
              {t("actionCenter", defaultContent.actionCenter as string)}
            </h2>
          </div>
          <div className="GroupManagementDashboard__section-description">
            <TimeStamp dateFormat="bigEndian" />
          </div>
        </div>

        <div className="GroupManagementDashboard__action-center">
          <ActionCenter items={groupManagementOverviewData.ActionReportItems} />
        </div>

        {navigationItemMap.has("customerMemberList") && (
          <div className="GroupManagementDashboard__customer-orders">
            <CustomerOrdersCard isOverviewLoading={isLoading} isCustomerListEnabled={isCustomerListEnabled} />
          </div>
        )}

        <div className="GroupManagementDashboard__navigation-tiles">
          {navigationTiles.map((tile) => (
            <div key={tile.to} className="GroupManagementDashboard__tile">
              <GroupNavigationTile
                title={getCanonicalTitle({ id: tile.navKey }, t, {})}
                iconType={tile.iconType}
                to={tile.to}
                disabled={!navigationItemMap.get(tile.id)}
                tealiumAction={tile.tealiumAction}
                tealiumCategory={tile.tealiumCategory}
              />
            </div>
          ))}
        </div>
      </div>
      <BonusInfoFooter hideDate />
    </>
  );
};
